import React from 'react'
import classNames from 'classnames'
import { DateFormat, moment } from '@pbt/pbt-ui-components'

import type { RxCardItem } from '~/types'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import { RxCardDetails } from './prescriptions/rxTracker/RxCardDetails'
import { computeRelativeTime } from './prescriptions/rxTracker/rxCardUtilities'
import RxCardHeader from './RxCardHeader'

import styles from './BaseRxCard.module.scss'

const RX_CARD_DATE_FORMAT = DateFormat.FULL_DATE_COMMA_PIPE_TIME

export interface BaseRxCardProps {
  actionNeeded?: boolean
  icon?: React.ComponentType<{ className?: string }>
  id?: string
  index: number
  isInvoice?: boolean
  item: RxCardItem
  onClick?: () => void
  renderAdditionalContent?: () => React.ReactNode
  titlePinColor?: string | null
}

/**
 * Base card component for displaying both prescriptions and ordered drugs.
 * Uses timeline rail styling (iconPaper/iconContainer) to maintain visual consistency.
 * RxCardHeader handles all header content including dates and title.
 */
export const BaseRxCard: React.FC<BaseRxCardProps> = ({
  actionNeeded,
  icon: IconComponent,
  id,
  index,
  isInvoice,
  item,
  onClick,
  titlePinColor,
  renderAdditionalContent,
}) => {
  const timezone = useCurrentBusinessTimezone()

  const itemDate =
    item.date ??
    item.prescription?.updatedAt ??
    item.prescription?.creationDate ??
    ''

  const dateTitle = itemDate
    ? moment.tz(itemDate, timezone).format(RX_CARD_DATE_FORMAT)
    : ''

  const subheader = itemDate ? computeRelativeTime(itemDate, timezone) : null

  return (
    <div className={styles.root} id={id}>
      {IconComponent && (
        <div className={styles.iconContainer}>
          <div
            className={classNames(styles.iconPaper, {
              [styles.actionNeededIconContainer]: actionNeeded,
            })}
          >
            <IconComponent
              className={classNames(styles.icon, {
                [styles.actionNeededIcon]: actionNeeded,
              })}
            />
          </div>
        </div>
      )}
      {IconComponent && (
        <div className={styles.iconContainerMobile}>
          <div
            className={classNames(styles.iconPaperMobile, {
              [styles.actionNeededIconContainer]: actionNeeded,
            })}
          >
            <IconComponent
              className={classNames(styles.icon, {
                [styles.actionNeededIcon]: actionNeeded,
              })}
            />
          </div>
        </div>
      )}
      <div className={styles.container}>
        <div
          className={classNames(styles.mainPaper, {
            [styles.actionNeededContainer]: actionNeeded,
          })}
        >
          <RxCardHeader
            actionNeeded={actionNeeded}
            dateTitle={dateTitle}
            item={item}
            subheader={subheader}
            titlePinColor={titlePinColor}
            onClick={onClick}
          />
          <RxCardDetails
            index={index}
            isDetailView={false}
            isInvoice={isInvoice}
            item={item}
          />
          {renderAdditionalContent?.()}
        </div>
      </div>
    </div>
  )
}
